import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { ASSETS } from "../lib/data";
import { Input } from "../components/ui/input";

function formatErr(detail) {
  if (!detail) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("gesung_token", data.access_token);
      nav("/admin");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain relative min-h-screen bg-charcoal grid place-items-center px-5">
      <Link to="/" className="absolute top-6 left-6 flex items-center gap-2 text-white/50 hover:text-lime2 transition-colors text-sm" data-testid="back-home">
        <ArrowLeft size={16} /> Back to site
      </Link>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#181818] p-8 md:p-10"
      >
        <img src={ASSETS.logo} alt="Gesung" className="h-12 w-12 rounded-md object-cover mb-6" />
        <h1 className="font-display font-extrabold tracking-tighter text-3xl">Admin Console</h1>
        <p className="text-white/50 text-sm mt-2">Sign in to manage leads and subscribers.</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@gesung.energy" data-testid="login-email" className="bg-charcoal border-white/15 h-12" />
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="login-password" className="bg-charcoal border-white/15 h-12" />
          {error && <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald2 hover:bg-lime2 hover:text-black text-white font-semibold rounded-full py-3.5 transition-colors duration-300 disabled:opacity-60"
          >
            <Lock size={16} /> {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
